import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Send,
  X,
  FileText,
  AlertTriangle,
  Scale,
  Shield,
  Lightbulb,
  CheckCircle,
  Copy,
  RefreshCw,
  BookOpen,
  Gavel,
  Loader2,
  ChevronDown,
  Zap,
  Award,
  Star
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Legal writing suggestions database
const WRITING_SUGGESTIONS = {
  compraventa: {
    title: 'Contrato de Compraventa',
    clauses: [
      { type: 'obligatoria', text: 'Identificación completa de las partes (RUT, domicilio, estado civil)' },
      { type: 'obligatoria', text: 'Descripción detallada del bien objeto de la venta' },
      { type: 'obligatoria', text: 'Precio y forma de pago claramente estipulados' },
      { type: 'recomendada', text: 'Cláusula de garantía de evicción' },
      { type: 'recomendada', text: 'Cláusula de vicios ocultos con plazo de reclamo' },
      { type: 'excepcion', text: 'Excepción: Si es entre parientes, considerar valor fiscal mínimo' }
    ],
    exceptions: [
      'Art. 1810 CC: No pueden venderse las cosas que están fuera del comercio',
      'Art. 1796 CC: Prohibición de venta entre cónyuges no separados judicialmente',
      'Art. 1797 CC: Prohibición de venta entre padre/hijo de familia'
    ],
    tips: [
      'Siempre verificar la titularidad del bien en registros públicos',
      'Incluir cláusula penal por incumplimiento',
      'Especificar quién asume los gastos notariales'
    ]
  },
  arriendo: {
    title: 'Contrato de Arriendo',
    clauses: [
      { type: 'obligatoria', text: 'Identificación del inmueble con dirección exacta' },
      { type: 'obligatoria', text: 'Monto de la renta y fecha de pago' },
      { type: 'obligatoria', text: 'Duración del contrato (plazo fijo o indefinido)' },
      { type: 'recomendada', text: 'Cláusula de reajuste según IPC' },
      { type: 'recomendada', text: 'Inventario detallado del inmueble' },
      { type: 'excepcion', text: 'Ley 18.101: Protección al arrendatario de viviendas' }
    ],
    exceptions: [
      'Art. 1943 CC: El arrendador debe entregar la cosa en estado de servir',
      'Art. 1973 CC: Desahucio judicial para terminar arrendamiento',
      'Ley 21.461: Nuevo procedimiento de desalojo'
    ],
    tips: [
      'Exigir mes de garantía o 2 meses de renta',
      'Incluir prohibición de subarriendo',
      'Fotografiar estado inicial del inmueble'
    ]
  },
  laboral: {
    title: 'Contrato de Trabajo',
    clauses: [
      { type: 'obligatoria', text: 'Lugar y fecha del contrato' },
      { type: 'obligatoria', text: 'Individualización de las partes' },
      { type: 'obligatoria', text: 'Naturaleza de los servicios y lugar de prestación' },
      { type: 'obligatoria', text: 'Monto, forma y período de pago de la remuneración' },
      { type: 'obligatoria', text: 'Duración y distribución de la jornada de trabajo' },
      { type: 'recomendada', text: 'Cláusula de confidencialidad' },
      { type: 'excepcion', text: 'Art. 22 CT: Trabajadores sin limitación de jornada' }
    ],
    exceptions: [
      'Art. 159 CT: Causales de terminación del contrato',
      'Art. 161 CT: Necesidades de la empresa',
      'Art. 171 CT: Despido indirecto',
      'Art. 163 CT: Indemnización por años de servicio'
    ],
    tips: [
      'Escriturar dentro de 15 días desde inicio de labores',
      'Actualizar ante cualquier modificación de condiciones',
      'Guardar copia firmada por ambas partes'
    ]
  },
  sociedad: {
    title: 'Constitución de Sociedad',
    clauses: [
      { type: 'obligatoria', text: 'Nombre, nacionalidad, domicilio y RUT de socios' },
      { type: 'obligatoria', text: 'Razón social y domicilio de la sociedad' },
      { type: 'obligatoria', text: 'Capital social y aportes de cada socio' },
      { type: 'obligatoria', text: 'Objeto social específico y determinado' },
      { type: 'obligatoria', text: 'Administración y representación legal' },
      { type: 'recomendada', text: 'Cláusula de resolución de conflictos (arbitraje)' },
      { type: 'excepcion', text: 'SpA puede tener un solo accionista' }
    ],
    exceptions: [
      'Art. 2053 CC: Sociedad nula si no tiene fin lícito',
      'Art. 2054 CC: Nulidad si no hay aporte de todos los socios',
      'Art. 424 Código Comercio: Publicación en Diario Oficial'
    ],
    tips: [
      'Considerar tipo societario según necesidades (Ltda, SpA, SA)',
      'Definir claramente la forma de distribución de utilidades',
      'Establecer mecanismos de entrada y salida de socios'
    ]
  }
};

// Risk analysis patterns
const RISK_PATTERNS = {
  high: [
    { pattern: /sin garantía/i, risk: 'Falta de garantía', solution: 'Agregar cláusula de garantía real o personal' },
    { pattern: /plazo indefinido/i, risk: 'Plazo indefinido', solution: 'Considerar establecer plazo determinado con renovación automática' },
    { pattern: /verbal/i, risk: 'Acuerdo verbal', solution: 'Escriturar siempre para tener prueba documental' },
    { pattern: /todo riesgo/i, risk: 'Asunción total de riesgo', solution: 'Limitar responsabilidad y excluir casos fortuitos' },
  ],
  medium: [
    { pattern: /renovación automática/i, risk: 'Renovación automática', solution: 'Agregar cláusula de término anticipado' },
    { pattern: /sin límite/i, risk: 'Sin límites establecidos', solution: 'Definir límites claros de responsabilidad' },
    { pattern: /cesión libre/i, risk: 'Cesión sin restricciones', solution: 'Requerir consentimiento para cesión' },
  ],
  protective: [
    { pattern: /cláusula penal/i, protection: 'Protección por incumplimiento' },
    { pattern: /arbitraje/i, protection: 'Resolución alternativa de conflictos' },
    { pattern: /garantía hipotecaria/i, protection: 'Garantía real sobre inmueble' },
    { pattern: /notaría/i, protection: 'Documento con fecha cierta' },
  ]
};

const LegalWritingAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState('suggest'); // suggest, analyze, exceptions
  const [selectedType, setSelectedType] = useState('compraventa');
  const [inputText, setInputText] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const { api, token } = useAuth();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, activeMode]);

  const contractTypes = [
    { id: 'compraventa', label: 'Compraventa', icon: FileText },
    { id: 'arriendo', label: 'Arriendo', icon: BookOpen },
    { id: 'laboral', label: 'Laboral', icon: Gavel },
    { id: 'sociedad', label: 'Sociedad', icon: Scale }
  ];

  const analyzeText = async () => {
    if (!inputText.trim()) {
      toast.error('Ingresa el texto del contrato a analizar');
      return;
    }

    setIsAnalyzing(true);
    
    // Simulate AI analysis with pattern matching
    await new Promise(resolve => setTimeout(resolve, 1500));

    const risks = [];
    const protections = [];
    let riskScore = 20;

    // Check for high risks
    RISK_PATTERNS.high.forEach(({ pattern, risk, solution }) => {
      if (pattern.test(inputText)) {
        risks.push({ level: 'alto', risk, solution });
        riskScore += 25;
      }
    });

    // Check for medium risks
    RISK_PATTERNS.medium.forEach(({ pattern, risk, solution }) => {
      if (pattern.test(inputText)) {
        risks.push({ level: 'medio', risk, solution });
        riskScore += 15;
      }
    });

    // Check for protections
    RISK_PATTERNS.protective.forEach(({ pattern, protection }) => {
      if (pattern.test(inputText)) {
        protections.push(protection);
        riskScore -= 10;
      }
    });

    riskScore = Math.max(0, Math.min(100, riskScore));

    // Try AI analysis if available
    if (token) {
      try {
        const response = await api.post('/chat', {
          message: `Analiza este texto de contrato y sugiere mejoras legales específicas para Chile. Identifica cláusulas faltantes, riesgos y excepciones legales aplicables. Texto: "${inputText.substring(0, 500)}..."`,
          context: 'Eres un abogado experto en derecho chileno. Responde de forma concisa y práctica.'
        });
        
        setAnalysisResult({
          riskScore,
          risks,
          protections,
          aiSuggestions: response.data.response,
          recommendations: generateRecommendations(risks, protections)
        });
      } catch (error) {
        setAnalysisResult({
          riskScore,
          risks,
          protections,
          aiSuggestions: null,
          recommendations: generateRecommendations(risks, protections)
        });
      }
    } else {
      setAnalysisResult({
        riskScore,
        risks,
        protections,
        aiSuggestions: null,
        recommendations: generateRecommendations(risks, protections)
      });
    }

    setIsAnalyzing(false);
  };

  const generateRecommendations = (risks, protections) => {
    const recs = [];
    
    if (risks.length > 0) {
      recs.push('Revisar las cláusulas de riesgo identificadas');
    }
    if (protections.length < 2) {
      recs.push('Agregar más cláusulas de protección (garantías, penalidades)');
    }
    recs.push('Verificar cumplimiento con normativa vigente');
    recs.push('Considerar revisión por abogado especialista');
    
    return recs;
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const currentSuggestions = WRITING_SUGGESTIONS[selectedType];

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 left-6 z-50 w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="legal-writing-assistant-btn"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90 }} animate={{ rotate: 0 }} exit={{ rotate: 90 }}>
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
              <Sparkles className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Assistant Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -20, scale: 0.95 }}
            className="fixed bottom-44 left-6 z-50 w-[420px] max-w-[calc(100vw-48px)] bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden"
            data-testid="legal-writing-assistant-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Asistente de Redacción Legal</h3>
                    <p className="text-purple-100 text-xs flex items-center space-x-1">
                      <Zap className="w-3 h-3" />
                      <span>IA Especializada en Derecho Chileno</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mode Tabs */}
            <div className="flex border-b border-slate-700">
              {[
                { id: 'suggest', label: 'Sugerencias', icon: Lightbulb },
                { id: 'analyze', label: 'Analizar', icon: AlertTriangle },
                { id: 'exceptions', label: 'Excepciones', icon: Scale }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => { setActiveMode(mode.id); setAnalysisResult(null); }}
                  className={`flex-1 flex items-center justify-center space-x-1 py-3 text-sm transition-colors ${
                    activeMode === mode.id
                      ? 'bg-slate-800 text-white border-b-2 border-purple-500'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <mode.icon className="w-4 h-4" />
                  <span>{mode.label}</span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="h-[400px] overflow-y-auto p-4">
              {/* Suggest Mode */}
              {activeMode === 'suggest' && (
                <div className="space-y-4">
                  {/* Contract Type Selector */}
                  <div>
                    <label className="text-xs text-slate-500 mb-2 block">Tipo de Contrato</label>
                    <div className="grid grid-cols-2 gap-2">
                      {contractTypes.map(type => (
                        <button
                          key={type.id}
                          onClick={() => setSelectedType(type.id)}
                          className={`flex items-center space-x-2 p-2 rounded-lg text-sm transition-colors ${
                            selectedType === type.id
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          <type.icon className="w-4 h-4" />
                          <span>{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Clauses */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-2 flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-purple-400" />
                      <span>Cláusulas Recomendadas</span>
                    </h4>
                    <div className="space-y-2">
                      {currentSuggestions?.clauses.map((clause, idx) => (
                        <div
                          key={idx}
                          className="flex items-start space-x-2 p-2 bg-slate-800/50 rounded-lg group"
                        >
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                            clause.type === 'obligatoria' ? 'bg-red-500' :
                            clause.type === 'recomendada' ? 'bg-yellow-500' : 'bg-blue-500'
                          }`} />
                          <div className="flex-1">
                            <p className="text-xs text-slate-300">{clause.text}</p>
                            <span className={`text-[10px] ${
                              clause.type === 'obligatoria' ? 'text-red-400' :
                              clause.type === 'recomendada' ? 'text-yellow-400' : 'text-blue-400'
                            }`}>
                              {clause.type.charAt(0).toUpperCase() + clause.type.slice(1)}
                            </span>
                          </div>
                          <button
                            onClick={() => copyToClipboard(clause.text, idx)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-white"
                          >
                            {copiedIndex === idx ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tips */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-2 flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400" />
                      <span>Tips de Experto</span>
                    </h4>
                    <div className="space-y-1">
                      {currentSuggestions?.tips.map((tip, idx) => (
                        <div key={idx} className="flex items-start space-x-2 text-xs text-slate-400">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Analyze Mode */}
              {activeMode === 'analyze' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-2 block">Pega el texto de tu contrato</label>
                    <textarea
                      ref={inputRef}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Pega aquí el texto del contrato para analizar riesgos, cláusulas faltantes y sugerencias de mejora..."
                      className="w-full h-32 bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-purple-500"
                      data-testid="contract-analysis-input"
                    />
                    <button
                      onClick={analyzeText}
                      disabled={isAnalyzing || !inputText.trim()}
                      className="w-full mt-2 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-colors"
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Analizando...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          <span>Analizar Contrato</span>
                        </>
                      )}
                    </button>
                  </div>

                  {analysisResult && (
                    <div className="space-y-4">
                      {/* Risk Score */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-400">Nivel de Riesgo</span>
                          <span className={`text-2xl font-bold ${
                            analysisResult.riskScore > 60 ? 'text-red-500' :
                            analysisResult.riskScore > 30 ? 'text-yellow-500' : 'text-green-500'
                          }`}>
                            {analysisResult.riskScore}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${analysisResult.riskScore}%` }}
                            className={`h-full rounded-full ${
                              analysisResult.riskScore > 60 ? 'bg-red-500' :
                              analysisResult.riskScore > 30 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Risks Found */}
                      {analysisResult.risks.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-white mb-2 flex items-center space-x-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            <span>Riesgos Detectados</span>
                          </h4>
                          <div className="space-y-2">
                            {analysisResult.risks.map((risk, idx) => (
                              <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                <p className="text-sm text-red-400 font-medium">{risk.risk}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  <span className="text-green-400">Solución:</span> {risk.solution}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Protections Found */}
                      {analysisResult.protections.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-white mb-2 flex items-center space-x-2">
                            <Shield className="w-4 h-4 text-green-400" />
                            <span>Protecciones Encontradas</span>
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {analysisResult.protections.map((protection, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                {protection}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Suggestions */}
                      {analysisResult.aiSuggestions && (
                        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-purple-400 mb-2 flex items-center space-x-2">
                            <Sparkles className="w-4 h-4" />
                            <span>Sugerencias de IA</span>
                          </h4>
                          <p className="text-xs text-slate-300 whitespace-pre-line">
                            {analysisResult.aiSuggestions}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Exceptions Mode */}
              {activeMode === 'exceptions' && (
                <div className="space-y-4">
                  {/* Contract Type Selector */}
                  <div>
                    <label className="text-xs text-slate-500 mb-2 block">Selecciona tipo de contrato</label>
                    <div className="grid grid-cols-2 gap-2">
                      {contractTypes.map(type => (
                        <button
                          key={type.id}
                          onClick={() => setSelectedType(type.id)}
                          className={`flex items-center space-x-2 p-2 rounded-lg text-sm transition-colors ${
                            selectedType === type.id
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          <type.icon className="w-4 h-4" />
                          <span>{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Exceptions List */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3 flex items-center space-x-2">
                      <Scale className="w-4 h-4 text-blue-400" />
                      <span>Excepciones Legales - {currentSuggestions?.title}</span>
                    </h4>
                    <div className="space-y-3">
                      {currentSuggestions?.exceptions.map((exception, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 group"
                        >
                          <div className="flex items-start justify-between">
                            <p className="text-xs text-slate-300">{exception}</p>
                            <button
                              onClick={() => copyToClipboard(exception, `exc-${idx}`)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-white ml-2"
                            >
                              {copiedIndex === `exc-${idx}` ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* General Exceptions */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-400 mb-2 flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Excepciones Generales Importantes</span>
                    </h4>
                    <ul className="text-xs text-slate-400 space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500">•</span>
                        <span><strong>Art. 1545 CC:</strong> Todo contrato legalmente celebrado es una ley para los contratantes</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500">•</span>
                        <span><strong>Art. 1546 CC:</strong> Contratos deben ejecutarse de buena fe</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500">•</span>
                        <span><strong>Art. 1560 CC:</strong> Conocida la intención, debe estarse a ella más que a lo literal</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500">•</span>
                        <span><strong>Art. 1683 CC:</strong> Nulidad absoluta por objeto o causa ilícita</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-700 bg-slate-900">
              <p className="text-center text-xs text-slate-500">
                Asistencia basada en legislación chilena vigente
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LegalWritingAssistant;
